import get from 'lodash/get';
import set from 'lodash/set';

import type { ConditionalRequestDefinition } from '@/utils/dehydrateRequestData/types';
import { ClientResource } from '@/features/Clients';
import { isDate } from '@/utils/dates/dates';

const removeUndefinedDeep = (
  obj: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [
        k,
        v === Object(v) && !isDate(v)
          ? removeUndefinedDeep(v as Record<string, unknown>)
          : v,
      ]),
  );

export const dehydrateRequestData = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  state: Record<string, unknown>,
  cleanings:
    | ConditionalRequestDefinition[]
    | (() => ConditionalRequestDefinition[]),
): T => {
  let hasPathsToDrop = false;

  const cleaningDefinitions = Array.isArray(cleanings)
    ? cleanings
    : cleanings();

  cleaningDefinitions.forEach((def) => {
    if (typeof def.if === 'function') {
      if (def.if(state as ClientResource).value) {
        def.drop.forEach((path) => {
          if (get(state, path) !== undefined) {
            set(state, path, undefined);
            hasPathsToDrop = true;
          }
        });
      }
    } else if (def.if.value) {
      def.drop.forEach((path) => {
        if (get(state, path) !== undefined) {
          set(state, path, undefined);
          hasPathsToDrop = true;
        }
      });
    }
  });

  if (hasPathsToDrop) {
    return removeUndefinedDeep(state) as T;
  }

  return state as T;
};
